/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, no-unused-vars */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import { FaArrowLeft, FaEye, FaDesktop, FaTabletAlt, FaMobileAlt, FaUndo, FaRedo, FaTrash, FaCheck, FaFileImport, FaTable, FaTimes, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import './ModifyBlogContent.css'; // Share the exact CSS file to guarantee identical styling!

const ModifyPageContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pageTitle, setPageTitle] = useState('Loading Page...');
  const [pageData, setPageData] = useState(null);
  const [editor, setEditor] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [activeSidebarTab, setActiveSidebarTab] = useState('blocks'); // 'blocks', 'styles', 'traits'
  const [activeDevice, setActiveDevice] = useState('desktop'); // 'desktop', 'tablet', 'mobile'

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const editorRef = useRef(null);

  useEffect(() => {
    fetchPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPageData = useCallback(async () => {
    try {
      const endpoint = String(id) === '10' ? '/pages/terms' : '/pages/privacy';
      const res = await axiosInstance.get(endpoint);
      if (res && res.data && res.data.success) {
        const p = res.data.data;
        setPageData(p);
        setPageTitle(p.page_heading || (String(id) === '10' ? 'Terms & Conditions' : 'Privacy Policy'));
      } else {
        toast.error('Page record not found');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load page content');
    }
  }, [id]);

  useEffect(() => {
    if (!pageData) return;
    if (!editorRef.current) return;

    const initialContent = pageData.description || '';

    // Initialize GrapesJS
    const gjsEditor = grapesjs.init({
      container: editorRef.current,
      fromElement: false,
      height: '100%',
      width: '100%',
      storageManager: false,
      panels: { defaults: [] }, // Custom outer controls
      deviceManager: {
        devices: [
          {
            id: 'desktop',
            name: 'Desktop',
            width: '100%'
          },
          {
            id: 'tablet',
            name: 'Tablet',
            width: '768px'
          },
          {
            id: 'mobile',
            name: 'Mobile portrait',
            width: '375px'
          }
        ]
      },
      blockManager: {
        appendTo: '#gjs-blocks-container',
        blocks: [
          {
            id: 'heading',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-heading block-icon"></i><div class="block-label">Heading</div></div>`,
            category: 'Base',
            content:
              '<h2 style="font-family: \'Poppins\', sans-serif; font-weight: 600; color: #c4a882; margin-bottom: 1rem;">Type your heading here</h2>',
            attributes: { class: 'gjs-block-custom-heading' }
          },
          {
            id: 'paragraph',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-paragraph block-icon"></i><div class="block-label">Paragraph</div></div>`,
            category: 'Base',
            content:
              '<p style="font-family: \'Poppins\', sans-serif; line-height: 1.6; color: #e5e5e5; margin-bottom: 1rem;">Our customers are of paramount importance to us & we greatly value the faith our customers have in us. Type paragraph here...</p>'
          },
          {
            id: 'image',
            label: `<div class="gjs-custom-block-content"><i class="fa-regular fa-image block-icon"></i><div class="block-label">Image</div></div>`,
            category: 'Base',
            content: { type: 'image' }
          },
          {
            id: 'blockquote',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-quote-right block-icon"></i><div class="block-label">Blockquote</div></div>`,
            category: 'Base',
            content:
              '<blockquote style="border-left: 4px solid #c4a882; padding-left: 1.5rem; margin: 1.5rem 0; font-style: italic; color: #a0a0a0; font-family: \'Poppins\', sans-serif;">This is a blockquote. Edit this text.</blockquote>'
          },
          {
            id: 'horizontal-rule',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-minus block-icon"></i><div class="block-label">Horizontal Rule</div></div>`,
            category: 'Base',
            content: '<hr style="border: 0; border-top: 1px solid rgba(196, 168, 130, 0.25); margin: 2rem 0;"/>'
          },
          {
            id: 'link',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-link block-icon"></i><div class="block-label">Link</div></div>`,
            category: 'Base',
            content: '<a href="#" style="color: #c4a882; text-decoration: underline;">Link Text</a>'
          },
          {
            id: 'list',
            label: `<div class="gjs-custom-block-content"><i class="fa-solid fa-list block-icon"></i><div class="block-label">List</div></div>`,
            category: 'Base',
            content:
              '<ul style="margin-bottom: 1rem; padding-left: 1.5rem; color: #e5e5e5;"><li style="margin-bottom: 0.5rem;">List item 1</li><li style="margin-bottom: 0.5rem;">List item 2</li></ul>'
          },
          {
            id: 'table',
            label: '<div class="gjs-custom-block-content"><i class="fa-solid fa-table block-icon"></i><div class="block-label">Table</div></div>',
            category: 'Base',
            content:
              '<div style="overflow-x: auto; margin: 1.5rem 0;"><table style="width: 100%; border-collapse: collapse; border: 1px solid rgba(196, 168, 130, 0.3); border-radius: 8px; overflow: hidden; background-color: rgba(255, 255, 255, 0.02);"><thead><tr style="background-color: rgba(196, 168, 130, 0.15); border-bottom: 1px solid rgba(196, 168, 130, 0.3);"><th style="padding: 12px 16px; text-align: left; font-weight: 600; color: #c4a882;">Stage</th><th style="padding: 12px 16px; text-align: center; font-weight: 600; color: #c4a882;">Payment (%)</th><th style="padding: 12px 16px; text-align: left; font-weight: 600; color: #c4a882;">Details</th></tr></thead><tbody><tr style="border-bottom: 1px solid rgba(255, 255, 255, 0.08);"><td style="padding: 12px 16px; color: #ffffff; font-weight: 500;">Order Booking</td><td style="padding: 12px 16px; text-align: center; color: #c4a882; font-weight: 600;">10%</td><td style="padding: 12px 16px; color: #d1d5db;">Shortlisted design proposal will be shared.</td></tr><tr style="border-bottom: 1px solid rgba(255, 255, 255, 0.08);"><td style="padding: 12px 16px; color: #ffffff; font-weight: 500;">Push to Production</td><td style="padding: 12px 16px; text-align: center; color: #c4a882; font-weight: 600;">40%</td><td style="padding: 12px 16px; color: #d1d5db;">Sale order &amp; Push to Production</td></tr><tr><td style="padding: 12px 16px; color: #ffffff; font-weight: 500;">Dispatch of Materials</td><td style="padding: 12px 16px; text-align: center; color: #c4a882; font-weight: 600;">50%</td><td style="padding: 12px 16px; color: #d1d5db;">Dispatch will be scheduled subject to Pre-installation clearance.</td></tr></tbody></table></div>'
          }
        ]
      },
      assetManager: {
        upload: `${process.env.REACT_APP_BACKEND || (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) || 'http://localhost:8000'}/api/v1/products/upload`,
        uploadName: 'image',
        params: {},
        headers: {},
        autoAdd: true,
        uploadResponse: (response) => {
          const backendHost = (
            process.env.REACT_APP_BACKEND ||
            (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) ||
            'http://localhost:8000'
          ).replace(/\/$/, '');
          const fileUrl = `${backendHost}/uploads/${response.filename}`;
          return {
            data: [fileUrl]
          };
        }
      },
      styleManager: {
        appendTo: '#gjs-style-container',
        sectors: [
          {
            name: 'General Layout',
            open: true,
            buildProps: ['display', 'position', 'top', 'right', 'left', 'bottom', 'float']
          },
          {
            name: 'Spacing & Size',
            open: true,
            buildProps: ['width', 'height', 'max-width', 'margin', 'padding']
          },
          {
            name: 'Typography',
            open: true,
            buildProps: ['font-family', 'font-size', 'font-weight', 'color', 'line-height', 'text-align']
          },
          {
            name: 'Decorations',
            open: false,
            buildProps: ['background-color', 'border', 'border-radius', 'box-shadow']
          }
        ]
      },
      traitManager: {
        appendTo: '#gjs-trait-container'
      },
      canvas: {
        styles: [
          'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap',
          'body, html { background-color: #121211 !important; margin: 0 !important; padding: 0 !important; height: 100% !important; width: 100% !important; font-family: "Poppins", sans-serif; }',
          '#wrapper, [data-gjs-type="wrapper"] { background-color: #121211 !important; color: #e5e5e5 !important; padding: 2rem 3rem !important; max-width: 100% !important; width: 100% !important; margin: 0 !important; min-height: 100vh !important; box-shadow: none !important; border-radius: 0 !important; }',
          '* { background-color: transparent !important; color: #e5e5e5 !important; }',
          'h1, h2, h3, h4, h5, h6, b, strong { color: #c4a882 !important; }',
          'a { color: #c4a882 !important; text-decoration: underline; }',
          'blockquote { color: #a0a0a0 !important; border-left: 4px solid #c4a882 !important; background-color: rgba(196, 168, 130, 0.05) !important; padding: 1rem !important; }',
          'table { width: 100% !important; border-collapse: collapse !important; margin: 1.5rem 0 !important; border: 1px solid rgba(196, 168, 130, 0.3) !important; border-radius: 8px !important; overflow: hidden !important; background-color: rgba(255, 255, 255, 0.02) !important; }',
          'th { background-color: rgba(196, 168, 130, 0.15) !important; color: #c4a882 !important; padding: 12px 16px !important; font-weight: 600 !important; border-bottom: 1px solid rgba(196, 168, 130, 0.3) !important; text-align: left !important; }',
          'td { padding: 12px 16px !important; border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important; color: #d1d5db !important; text-align: left !important; }'
        ]
      }
    });

    const cleanHtmlContent = (html) => {
      if (!html) return '';
      let cleaned = html.replace(/<link[^>]*stylesheet[^>]*>/gi, '');
      cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch && bodyMatch[1]) {
        return bodyMatch[1].trim();
      }
      return cleaned.replace(/<\/?(html|head|body)[^>]*>/gi, '').trim();
    };

    if (initialContent) {
      gjsEditor.setComponents(cleanHtmlContent(initialContent));
    }

    setEditor(gjsEditor);

    gjsEditor.on('load', () => {
      const css = gjsEditor.Css;
      if (css) {
        css.setRule('#wrapper, .gjs-wrapper, [data-gjs-type="wrapper"]', {
          'background-color': '#121211 !important',
          color: '#e5e5e5 !important',
          padding: '2.5rem 6% !important',
          width: '100% !important',
          'max-width': '1100px !important',
          margin: '2rem auto !important',
          'min-height': 'calc(100vh - 120px) !important',
          'box-shadow': '0 10px 40px rgba(0, 0, 0, 0.6) !important',
          'border-radius': '8px !important',
          'box-sizing': 'border-box !important',
          'text-align': 'left !important'
        });

        css.setRule('body, html', {
          'background-color': '#080807 !important',
          margin: '0 !important',
          padding: '0 !important',
          width: '100% !important',
          height: '100% !important',
          'text-align': 'left !important'
        });

        css.setRule('span[id^="docs-internal-guid-"]', {
          display: 'block !important',
          width: '100% !important',
          'text-align': 'left !important'
        });

        css.setRule('p, span, li, td, th, strong, em, b, i, font, a', {
          color: '#e5e5e5 !important',
          'font-size': '1.05rem !important',
          'line-height': '1.7 !important',
          'text-align': 'left !important'
        });

        css.setRule('h1, h2, h3, h4, h5, h6, h1 *, h2 *, h3 *, h4 *, h5 *, h6 *', {
          color: '#c4a882 !important',
          'font-size': '1.75rem !important',
          'text-align': 'left !important'
        });

        css.setRule('blockquote', {
          color: '#a0a0a0 !important',
          'border-left': '4px solid #c4a882 !important',
          'background-color': 'rgba(196, 168, 130, 0.05) !important',
          padding: '1.25rem !important',
          margin: '1.5rem 0 !important',
          'text-align': 'left !important'
        });

        css.setRule('[class*="editor-width-"], .editor-margin-auto, .col-md-12, .row, .container, .container-fluid', {
          width: '100% !important',
          'max-width': '100% !important',
          margin: '0 !important',
          padding: '0 !important',
          'background-color': 'transparent !important',
          'text-align': 'left !important'
        });

        css.setRule('#wrapper > *, .gjs-wrapper > *, [data-gjs-type="wrapper"] > *', {
          width: '100% !important',
          'max-width': '100% !important',
          'margin-left': '0 !important',
          'margin-right': '0 !important',
          'text-align': 'left !important'
        });

        css.setRule('img', {
          'max-width': '100% !important',
          height: 'auto !important',
          display: 'block !important',
          margin: '1.5rem 0 !important'
        });

        css.setRule('table', {
          width: '100% !important',
          'border-collapse': 'collapse !important',
          margin: '1.5rem 0 !important',
          border: '1px solid rgba(196, 168, 130, 0.3) !important',
          'border-radius': '8px !important',
          overflow: 'hidden !important',
          'background-color': 'rgba(255, 255, 255, 0.02) !important'
        });

        css.setRule('th', {
          'background-color': 'rgba(196, 168, 130, 0.15) !important',
          color: '#c4a882 !important',
          padding: '12px 16px !important',
          'font-weight': '600 !important',
          'border-bottom': '1px solid rgba(196, 168, 130, 0.3) !important',
          'text-align': 'left !important'
        });

        css.setRule('td', {
          padding: '12px 16px !important',
          'border-bottom': '1px solid rgba(255, 255, 255, 0.08) !important',
          color: '#d1d5db !important',
          'text-align': 'left !important'
        });
      }
    });

    gjsEditor.on('component:selected', () => {
      setActiveSidebarTab('styles');
    });

    gjsEditor.on('run:preview:before', () => {
      setIsPreviewMode(true);
    });
    gjsEditor.on('stop:preview:before', () => {
      setIsPreviewMode(false);
    });

    // Enhance built-in RTE toolbar with rich actions
    const rte = gjsEditor.RichTextEditor;
    if (rte) {
      rte.add('bold', {
        icon: '<b>B</b>',
        attributes: { title: 'Bold' },
        result: (r) => r.exec('bold')
      });
      rte.add('italic', {
        icon: '<i>I</i>',
        attributes: { title: 'Italic' },
        result: (r) => r.exec('italic')
      });
      rte.add('underline', {
        icon: '<u>U</u>',
        attributes: { title: 'Underline' },
        result: (r) => r.exec('underline')
      });
      rte.add('insertOrderedList', {
        icon: '<i class="fa-solid fa-list-ol"></i>',
        attributes: { title: 'Numbered List' },
        result: (r) => r.exec('insertOrderedList')
      });
      rte.add('insertUnorderedList', {
        icon: '<i class="fa-solid fa-list-ul"></i>',
        attributes: { title: 'Bullet List' },
        result: (r) => r.exec('insertUnorderedList')
      });
      rte.add('justifyLeft', {
        icon: '<i class="fa-solid fa-align-left"></i>',
        attributes: { title: 'Align Left' },
        result: (r) => r.exec('justifyLeft')
      });
      rte.add('justifyCenter', {
        icon: '<i class="fa-solid fa-align-center"></i>',
        attributes: { title: 'Align Center' },
        result: (r) => r.exec('justifyCenter')
      });
      rte.add('justifyRight', {
        icon: '<i class="fa-solid fa-align-right"></i>',
        attributes: { title: 'Align Right' },
        result: (r) => r.exec('justifyRight')
      });
      rte.add('removeFormat', {
        icon: '<span>Tx</span>',
        attributes: { title: 'Clear Formatting' },
        result: (r) => r.exec('removeFormat')
      });
    }

    return () => {
      if (gjsEditor) {
        gjsEditor.destroy();
      }
    };
  }, [pageData]);

  
  // Helper to convert Markdown, plain text tables, or HTML into rich components
  const convertMarkdownOrTextToHtml = (text) => {
    if (!text) return '';
    const trimmed = text.trim();
    // If it is already full HTML, clean and return
    if (/<[a-z][\s\S]*>/i.test(trimmed)) {
      let cleaned = trimmed.replace(/<link[^>]*stylesheet[^>]*>/gi, '');
      cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch && bodyMatch[1]) {
        return bodyMatch[1].trim();
      }
      return cleaned.replace(/<\/?(html|head|body)[^>]*>/gi, '').trim();
    }

    const lines = trimmed.split(/\r?\n/);
    const output = [];
    let inList = false;
    let listType = 'ul';
    let inTable = false;
    let tableRows = [];

    const flushTable = () => {
      if (!inTable || tableRows.length === 0) return;
      let html = '<div style="overflow-x:auto; margin: 1.5rem 0;"><table style="width:100%; border-collapse:collapse; border:1px solid rgba(196,168,130,0.3); border-radius:8px; overflow:hidden; background-color:rgba(255,255,255,0.02);">';
      let isHeader = true;
      for (const r of tableRows) {
        if (r.every((c) => /^[-:\s]+$/.test(c))) {
          isHeader = false;
          continue;
        }
        if (isHeader) {
          html += '<thead style="background-color:rgba(196,168,130,0.15); border-bottom:1px solid rgba(196,168,130,0.3); color:#c4a882;"><tr>';
          r.forEach((col) => {
            html += `<th style="padding:12px 16px; text-align:left; font-weight:600; color:#c4a882;">${col}</th>`;
          });
          html += '</tr></thead><tbody>';
          isHeader = false;
        } else {
          html += '<tr style="border-bottom:1px solid rgba(255,255,255,0.08);">';
          r.forEach((col, idx) => {
            const isHighlight = col.includes('%') || idx === 1;
            html += `<td style="padding:12px 16px; color:${isHighlight ? '#c4a882' : '#e5e5e5'}; font-weight:${isHighlight ? '600' : 'normal'};">${col}</td>`;
          });
          html += '</tr>';
        }
      }
      if (html.includes('<tbody>')) {
        html += '</tbody>';
      }
      html += '</table></div>';
      output.push(html);
      tableRows = [];
      inTable = false;
    };

    const flushList = () => {
      if (inList) {
        output.push(`</${listType}>`);
        inList = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const rawLine = lines[i];
      const line = rawLine.trim();

      if (!line) {
        flushTable();
        flushList();
        continue;
      }

      // Check table row e.g. | Stage | Payment | Details |
      if (line.startsWith('|') && line.endsWith('|')) {
        flushList();
        inTable = true;
        const cells = line
          .split('|')
          .slice(1, -1)
          .map((c) => c.trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'));
        tableRows.push(cells);
        continue;
      } else if (inTable) {
        flushTable();
      }

      // Headings
      if (line.startsWith('#### ')) {
        flushList();
        output.push(`<h4 style="font-family:'Poppins',serif; font-size:1.35rem; color:#ffffff; margin-top:2rem; margin-bottom:0.75rem; border-bottom:1px solid rgba(255,255,255,0.1); padding-bottom:0.5rem;">${line.slice(5).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</h4>`);
        continue;
      }
      if (line.startsWith('### ')) {
        flushList();
        output.push(`<h3 style="font-family:'Poppins',serif; font-size:1.55rem; color:#c4a882; margin-top:2rem; margin-bottom:0.75rem;">${line.slice(4).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</h3>`);
        continue;
      }
      if (line.startsWith('## ')) {
        flushList();
        output.push(`<h2 style="font-family:'Poppins',serif; font-size:1.75rem; color:#c4a882; margin-top:2rem; margin-bottom:1rem;">${line.slice(3).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</h2>`);
        continue;
      }

      // Bullet lists
      if (/^[-*•]\s+/.test(line)) {
        if (!inList || listType !== 'ul') {
          flushList();
          output.push('<ul style="margin-bottom:1rem; padding-left:1.5rem; color:#e5e5e5; list-style-type:disc;">');
          inList = true;
          listType = 'ul';
        }
        const content = line.replace(/^[-*•]\s+/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        output.push(`<li style="margin-bottom:0.4rem; color:#d1d5db; line-height:1.6;">${content}</li>`);
        continue;
      }

      // Numbered lists
      if (/^\d+\.\s+/.test(line)) {
        if (!inList || listType !== 'ol') {
          flushList();
          output.push('<ol style="margin-bottom:1rem; padding-left:1.5rem; color:#e5e5e5; list-style-type:decimal;">');
          inList = true;
          listType = 'ol';
        }
        const content = line.replace(/^\d+\.\s+/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        output.push(`<li style="margin-bottom:0.4rem; color:#d1d5db; line-height:1.6;">${content}</li>`);
        continue;
      }

      // Regular paragraph
      flushList();
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      output.push(`<p style="font-family:'Poppins',sans-serif; line-height:1.7; color:#e5e5e5; margin-bottom:1rem;">${formatted}</p>`);
    }

    flushTable();
    flushList();

    return output.join('\n');
  };

  const handleApplyImport = (mode = 'replace') => {
    if (!editor || !importText.trim()) {
      toast.warn('Please paste some content first');
      return;
    }
    try {
      const parsedHtml = convertMarkdownOrTextToHtml(importText);
      if (mode === 'replace') {
        editor.setComponents(parsedHtml);
      } else {
        editor.addComponents(parsedHtml);
      }
      setIsImportModalOpen(false);
      setImportText('');
      toast.success('Content imported successfully! You can now edit any text or table cell visually.');
    } catch (err) {
      console.error('Import error:', err);
      toast.error('Failed to import: ' + (err.message || 'Invalid format'));
    }
  };

  // Rich Text Formatting Action Handler
  const handleFormatText = (command, value = null) => {
    if (!editor) return;

    try {
      const doc = editor.Canvas?.getDocument();
      if (doc) {
        doc.designMode = 'on';
        if (command === 'formatBlock') {
          doc.execCommand('formatBlock', false, value || '<p>');
        } else if (command === 'fontSize') {
          const fontVal = value === 'small' ? '2' : value === 'normal' ? '3' : value === 'large' ? '5' : value === 'huge' ? '6' : value;
          doc.execCommand('fontSize', false, fontVal);
        } else if (command === 'createLink') {
          const url = prompt('Enter URL:', 'https://');
          if (url) doc.execCommand('createLink', false, url);
        } else {
          doc.execCommand(command, false, value);
        }
      }

      // Also apply to currently selected component
      const selected = editor.getSelected();
      if (selected) {
        const styles = selected.getStyle() || {};
        if (command === 'bold') {
          const isBold = styles['font-weight'] === 'bold' || styles['font-weight'] === '700' || styles['font-weight'] === 700;
          selected.addStyle({ 'font-weight': isBold ? 'normal' : 'bold' });
        } else if (command === 'italic') {
          const isItalic = styles['font-style'] === 'italic';
          selected.addStyle({ 'font-style': isItalic ? 'normal' : 'italic' });
        } else if (command === 'underline') {
          const isUnderline = styles['text-decoration'] === 'underline';
          selected.addStyle({ 'text-decoration': isUnderline ? 'none' : 'underline' });
        } else if (command === 'justifyLeft') {
          selected.addStyle({ 'text-align': 'left' });
        } else if (command === 'justifyCenter') {
          selected.addStyle({ 'text-align': 'center' });
        } else if (command === 'justifyRight') {
          selected.addStyle({ 'text-align': 'right' });
        } else if (command === 'justifyFull') {
          selected.addStyle({ 'text-align': 'justify' });
        } else if (command === 'formatBlock' && value) {
          const tag = value.replace(/[<>]/g, '');
          selected.set('tagName', tag);
          if (tag.startsWith('h')) {
            selected.addStyle({ color: '#c4a882', 'font-weight': '600' });
          }
        } else if (command === 'fontSize' && value) {
          const sizeMap = { small: '13px', normal: '16px', large: '22px', huge: '32px' };
          if (sizeMap[value]) {
            selected.addStyle({ 'font-size': sizeMap[value] });
          }
        } else if (command === 'removeFormat') {
          selected.setStyle({});
        }
      }
    } catch (err) {
      console.warn('Formatting execution:', err);
    }
  };

  // Editor Actions
  const handleDeviceChange = (device) => {
    if (editor) {
      editor.setDevice(device);
      setActiveDevice(device);
    }
  };

  const handleUndo = () => editor && editor.UndoManager.undo();
  const handleRedo = () => editor && editor.UndoManager.redo();
  const handleClearCanvas = () => {
    if (editor && window.confirm('Are you sure you want to clear the canvas content?')) {
      editor.setComponents('');
    }
  };

  const handlePreview = () => {
    if (editor) {
      if (editor.Commands.isActive('preview')) {
        editor.stopCommand('preview');
      } else {
        editor.runCommand('preview');
      }
    }
  };

  const handleViewPage = () => {
    if (!editor) return;
    const html = editor.getHtml();
    const css = editor.getCss();

    const previewHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>${pageTitle} - Preview</title>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
          body {
            font-family: 'Poppins', sans-serif;
            background-color: #ffffff;
            color: #222222;
            margin: 0;
            padding: 3rem 2rem;
            max-width: 1000px;
            margin: 0 auto;
            line-height: 1.6;
          }
          img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 1.5rem auto;
            border-radius: 8px;
          }
          ${css}
        </style>
      </head>
      <body>
        ${html}
      </body>
      </html>
    `;

    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.open();
      newWindow.document.write(previewHtml);
      newWindow.document.close();
    } else {
      toast.error('Popup blocked! Please allow popups to view the page.');
    }
  };

  const handlePublish = async () => {
    if (!editor || !pageData) return;

    const html = editor.getHtml();
    const css = editor.getCss();
    const compiledContent = `<style>${css}</style>${html}`;

    try {
      const payload = {
        page_heading: pageData.page_heading,
        short_description: pageData.short_description,
        description: compiledContent,
        status: pageData.status,
        banner: pageData.banner,
        banner_status: pageData.banner_status,
        meta_title: pageData.meta_title,
        meta_description: pageData.meta_description,
        meta_keywords: pageData.meta_keywords
      };

      const endpoint = String(id) === '10' ? '/pages/terms' : '/pages/privacy';
      const res = await axiosInstance.patch(endpoint, payload);
      if (res.data && res.data.success) {
        toast.success('Page content published successfully!');
        navigate('/page-list');
      } else {
        toast.error(res.data.message || 'Failed to publish page content');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error publishing page content');
    }
  };

  return (
    <div className="builder-page-wrapper animate-fade-up">
      {/* Top Breadcrumb Navigation */}
      <div className="builder-breadcrumbs">
        <span onClick={() => navigate('/dashboard')} className="clickable">
          Dashboard
        </span>{' '}
        /{' '}
        <span onClick={() => navigate('/page-list')} className="clickable">
          Page Manager
        </span>{' '}
        / <span className="active">{pageTitle} Content</span>
      </div>

      {/* Main Panel Header */}
      <div className="builder-header-panel">
        {/* Left Side: History Controls */}
        <div className="header-left-group">
          <div className="toolbar-group">
            <button className="toolbar-btn" type="button" onClick={handleUndo} title="Undo">
              <FaUndo />
            </button>
            <button className="toolbar-btn" type="button" onClick={handleRedo} title="Redo">
              <FaRedo />
            </button>
            <button className="toolbar-btn text-danger" type="button" onClick={handleClearCanvas} title="Clear Canvas">
              <FaTrash />
            </button>
          </div>
          {/* Preview action */}
          <button className="toolbar-btn" type="button" onClick={handlePreview} title="Live Preview">
            <FaEye style={{ marginRight: '6px' }} /> Preview
          </button>
          <button
            className="toolbar-btn"
            type="button"
            onClick={() => setIsImportModalOpen(true)}
            title="Import or Paste Full Document Content"
            style={{
              background: 'rgba(196, 168, 130, 0.18)',
              borderColor: '#c4a882',
              color: '#c4a882',
              fontWeight: 500,
              padding: '0 12px',
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FaFileImport /> Import / Paste Content
          </button>
        </div>

        {/* Center: Formatting Toolbar (Bold, Italic, Underline, Heading, Size, Lists, Align, Clear) */}
        <div className="header-formatting-group">
          <div className="toolbar-group formatting-toolbar">
            <button
              className="toolbar-btn format-btn"
              type="button"
              onClick={() => handleFormatText('bold')}
              title="Bold"
              style={{ fontWeight: 'bold', fontSize: '14px' }}
            >
              B
            </button>
            <button
              className="toolbar-btn format-btn"
              type="button"
              onClick={() => handleFormatText('italic')}
              title="Italic"
              style={{ fontStyle: 'italic', fontFamily: 'serif', fontSize: '14px' }}
            >
              I
            </button>
            <button
              className="toolbar-btn format-btn"
              type="button"
              onClick={() => handleFormatText('underline')}
              title="Underline"
              style={{ textDecoration: 'underline', fontSize: '14px' }}
            >
              U
            </button>

            {/* Heading Dropdown */}
            <div className="format-select-wrapper">
              <select
                className="format-select"
                onChange={(e) => {
                  handleFormatText('formatBlock', e.target.value);
                  e.target.value = '';
                }}
                defaultValue=""
                title="Heading Style"
              >
                <option value="" disabled>
                  Normal
                </option>
                <option value="<h1>">Heading 1</option>
                <option value="<h2>">Heading 2</option>
                <option value="<h3>">Heading 3</option>
                <option value="<p>">Normal</option>
              </select>
            </div>

            {/* Font Size Dropdown */}
            <div className="format-select-wrapper">
              <select
                className="format-select"
                onChange={(e) => {
                  handleFormatText('fontSize', e.target.value);
                  e.target.value = '';
                }}
                defaultValue=""
                title="Font Size"
              >
                <option value="" disabled>
                  Normal
                </option>
                <option value="small">Small</option>
                <option value="normal">Normal</option>
                <option value="large">Large</option>
                <option value="huge">Huge</option>
              </select>
            </div>

            {/* Numbered List */}
            <button
              className="toolbar-btn format-btn"
              type="button"
              onClick={() => handleFormatText('insertOrderedList')}
              title="Numbered List"
            >
              <i className="fa-solid fa-list-ol" style={{ fontSize: '13px' }}></i>
            </button>

            {/* Bullet List */}
            <button
              className="toolbar-btn format-btn"
              type="button"
              onClick={() => handleFormatText('insertUnorderedList')}
              title="Bullet List"
            >
              <i className="fa-solid fa-list-ul" style={{ fontSize: '13px' }}></i>
            </button>

            {/* Text Alignments */}
            <div className="format-select-wrapper">
              <select
                className="format-select"
                onChange={(e) => {
                  handleFormatText(e.target.value);
                }}
                defaultValue="justifyLeft"
                title="Text Alignment"
              >
                <option value="justifyLeft">Left</option>
                <option value="justifyCenter">Center</option>
                <option value="justifyRight">Right</option>
                <option value="justifyFull">Justify</option>
              </select>
            </div>

            {/* Clear Formatting Tx */}
            <button
              className="toolbar-btn format-btn"
              type="button"
              onClick={() => handleFormatText('removeFormat')}
              title="Clear Formatting"
            >
              <span style={{ fontSize: '13px', fontWeight: 'bold' }}>
                T<sub style={{ fontSize: '9px' }}>x</sub>
              </span>
            </button>
          </div>
        </div>

        {/* Center: Device Viewports & View Page */}
        <div className="header-center-group">
          <button className="btn-view-page" type="button" onClick={handleViewPage} title="View Published Page">
            <i className="fa-solid fa-arrow-up-right-from-square" style={{ marginRight: '6px' }}></i> View page
          </button>
          <div className="toolbar-group">
            <button
              className={`toolbar-btn ${activeDevice === 'mobile' ? 'active' : ''}`}
              type="button"
              onClick={() => handleDeviceChange('mobile')}
              title="Mobile view"
            >
              <FaMobileAlt />
            </button>
            <button
              className={`toolbar-btn ${activeDevice === 'tablet' ? 'active' : ''}`}
              type="button"
              onClick={() => handleDeviceChange('tablet')}
              title="Tablet view"
            >
              <FaTabletAlt />
            </button>
            <button
              className={`toolbar-btn ${activeDevice === 'desktop' ? 'active' : ''}`}
              type="button"
              onClick={() => handleDeviceChange('desktop')}
              title="Desktop view"
            >
              <FaDesktop />
            </button>
          </div>
        </div>

        {/* Right: Publish Button */}
        <div className="publish-actions-row">
          <button className="btn-publish" type="button" onClick={handlePublish}>
            <FaCheck style={{ marginRight: '8px' }} /> Publish Now
          </button>
        </div>
      </div>

      {/* Workspace Grid */}
      <div className="builder-editor-container">
        {/* Sidebar blocks column */}
        {!isPreviewMode && (
          <div className="builder-sidebar-blocks">
            <div className="sidebar-tabs-header">
              <div className={`tab-item ${activeSidebarTab === 'blocks' ? 'active' : ''}`} onClick={() => setActiveSidebarTab('blocks')}>
                Blocks
              </div>
              <div className={`tab-item ${activeSidebarTab === 'styles' ? 'active' : ''}`} onClick={() => setActiveSidebarTab('styles')}>
                Styles
              </div>
              <div className={`tab-item ${activeSidebarTab === 'traits' ? 'active' : ''}`} onClick={() => setActiveSidebarTab('traits')}>
                Settings
              </div>
            </div>

            <div className="sidebar-blocks-content">
              {/* Blocks selector */}
              <div style={{ display: activeSidebarTab === 'blocks' ? 'block' : 'none' }}>
                <small className="blocks-section-title">Drag & Drop Blocks</small>
                <div id="gjs-blocks-container"></div>
              </div>

              {/* Styles manager */}
              <div style={{ display: activeSidebarTab === 'styles' ? 'block' : 'none' }}>
                <small className="blocks-section-title">Element Styles</small>
                <div id="gjs-style-container"></div>
              </div>

              {/* Settings / Trait manager */}
              <div style={{ display: activeSidebarTab === 'traits' ? 'block' : 'none' }}>
                <small className="blocks-section-title">Element Settings</small>
                <div id="gjs-trait-container"></div>
              </div>
            </div>
          </div>
        )}

        {/* Canvas editor container */}
        <div className="builder-canvas-viewport">
          <div ref={editorRef} id="gjs"></div>
        </div>
      </div>
    
      {/* Import / Paste Modal */}
      {isImportModalOpen && (
        <div className="builder-modal-overlay">
          <div className="builder-modal-card">
            <div className="builder-modal-header">
              <h3 className="builder-modal-title">
                <FaFileImport /> Import / Paste Page Content
              </h3>
              <button
                type="button"
                className="builder-modal-close"
                onClick={() => setIsImportModalOpen(false)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="builder-modal-body">
              <p className="builder-modal-desc">
                Paste your full content here (either <strong>HTML code</strong> or <strong>Formatted text / Markdown with tables</strong>). It will automatically convert into editable visual sections and styled luxury tables on your canvas!
              </p>
              <textarea
                className="builder-modal-textarea"
                placeholder="Paste your content or HTML here..."
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
            </div>

            <div className="builder-modal-footer">
              <button
                type="button"
                className="builder-modal-btn-cancel"
                onClick={() => setIsImportModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="builder-modal-btn-cancel"
                onClick={() => handleApplyImport('append')}
                title="Add to existing canvas content"
              >
                <FaPlus style={{ marginRight: '5px' }} /> Append to Canvas
              </button>
              <button
                type="button"
                className="builder-modal-btn-apply"
                onClick={() => handleApplyImport('replace')}
                title="Replace everything on canvas with this content"
              >
                <FaCheck /> Replace &amp; Apply to Canvas
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ModifyPageContent;
